/**
 * MyCapabilitiesPage — tenant self-service view of capability allocations.
 *
 * WQ-231 (Phase 4.9 α5). Mirrors the operator AllocationsPage but renders
 * AllocationsTab in readOnly mode against /api/tenant/me/capabilities/*
 * (consumptionService) instead of /api/admin/ai/allocations/*.
 *
 * Tenant scope is JWT-derived on the backend; we read tenantId from the
 * AuthContext to scope the UI's render path. Polling refreshes usage every
 * 30 seconds via AllocationsTab's existing interval.
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import AllocationsTab from '../components/ai/AllocationsTab';
import { consumptionService } from '../services/consumptionService';
import TenantKillSwitchControl from '../components/tenants/TenantKillSwitchControl';

const MyCapabilitiesPage: React.FC = () => {
  const { tenantId, user } = useAuth();
  const userEmail = (user as { email?: string } | null)?.email || '';

  if (!tenantId) {
    return (
      <div className="page-container" style={{ padding: '24px' }} data-testid="my-capabilities-no-tenant">
        <header style={{ marginBottom: '20px' }}>
          <h1 style={{ marginBottom: '4px' }}>My Capabilities</h1>
        </header>
        <div
          role="alert"
          style={{
            padding: '12px 16px',
            background: 'rgba(220,38,38,0.1)',
            border: '1px solid rgba(220,38,38,0.4)',
            borderRadius: '6px',
            color: 'var(--danger, #dc2626)',
          }}
        >
          A tenant context is required to view capabilities. Please sign in
          again or contact your administrator.
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ padding: '24px' }} data-testid="my-capabilities-page">
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ marginBottom: '4px' }}>My Capabilities</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
          The AI capabilities your organization has access to. Usage refreshes
          every 30 seconds. Quota changes are managed by your administrator.
        </p>
      </header>
      <AllocationsTab
        tenantId={tenantId}
        readOnly
        serviceOverride={consumptionService}
        currentUserEmail={userEmail}
      />
      {/* WQ-232 — per-tenant per-capability kill switch (sibling to allocations) */}
      <TenantKillSwitchControl />
    </div>
  );
};

export default MyCapabilitiesPage;
